import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from './cn';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  description?: ReactNode;
  hasError?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, hasError = false, className, id, ...rest },
  ref,
) {
  const wrapperId = id ?? rest.name;

  return (
    <label htmlFor={wrapperId} className="flex items-start gap-2 cursor-pointer select-none">
      <input
        ref={ref}
        id={wrapperId}
        type="checkbox"
        aria-invalid={hasError || undefined}
        className={cn(
          // `shrink-0` is load-bearing, not decoration. This input is a flex item
          // inside `flex items-start gap-2`, and flex items default to
          // `flex-shrink: 1` — while `w-4` sets a size, not a MINIMUM size. So a
          // long sibling description squashes the box, and the amount it squashes
          // by depends on how much text sits next to it.
          //
          // Measured on the US signup credit step, 2026-08-27: three checkboxes
          // with identical classes rendered 13px, 13px and 16px. The two with a
          // two-line description under their label were compressed; the third,
          // label-only, was the sole one at its true size. It was reported as
          // "the third checkbox is too big" — it was the only correct one.
          'mt-0.5 h-4 w-4 shrink-0 rounded border-border text-primary',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          hasError && 'border-danger',
          className,
        )}
        {...rest}
      />
      {(label || description) && (
        <span className="flex flex-col">
          {label && <span className="text-sm text-foreground">{label}</span>}
          {description && <span className="text-xs text-muted">{description}</span>}
        </span>
      )}
    </label>
  );
});
